import { useEffect, useState } from "react";
import {
  Button,
  CardHeader,
  DataGrid,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Icon,
  Loading,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Chip,
  Box,
  Typography,
  IconButton,
} from "../../../../../components";
import { OrcamentosService, ProdutosService } from "../../../../../services";
import { formatDate, formatPrice, responseErros } from "../../../../../utils";
import { format } from "date-fns";
import { calcularValorTotal } from "../../addOrcamentos";

const filtrosIniciasPaginacao = {
  page: 1,
  limit: 10,
  orderBy: "updatedAt",
  order: "desc",
};

const HistorioClienteDialog = ({
  open,
  setOpen,
  clienteId,
  orcamentos,
  setOrcamentos,
  totalItems,
  setTotalItems,
}) => {
  const orcamentosService = new OrcamentosService();
  const produtosService = new ProdutosService();
  const [loading, setLoading] = useState(false);
  const [itens, setItens] = useState([]);
  const [orcamentoselecionado, setOrcamentoselecionado] = useState(null);
  const [filtros, setFiltros] = useState({
    ...filtrosIniciasPaginacao,
  });
  const [query, setQuery] = useState("");

  const colunasItens = [
    {
      field: "codigo",
      headerName: "Cod.",
      flex: 100,
    },
    {
      field: "descricao",
      headerName: "Produto",
      flex: 400,
    },
    {
      field: "quantidade",
      headerName: "Quantidade",
      headerAlign: "center",
      align: "center",
      width: 150,
    },
    {
      field: "valorUnitario",
      headerName: "Unitário R$",
      headerAlign: "right",
      align: "right",
      flex: 200,
      valueGetter: (params) => formatPrice(parseFloat(params.value), false),
    },
    {
      field: "totalItem",
      headerName: "Total dos Itens R$",
      flex: 300,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        params.value ? formatPrice(parseFloat(params.value), false) : "",
    },
  ];

  useEffect(() => {
    if (open && clienteId) {
      const filtrosTemp = {
        ...filtros,
        pessoaClienteId: clienteId,
      };
      buscarOrcamentos(filtrosTemp);
    }
  }, [open, clienteId, filtros]);

  const buscarOrcamentos = async (filtros) => {
    setLoading(true);
    const result = await orcamentosService.getAll(filtros);
    if (!result.isAxiosError) {
      setOrcamentos(result.data.rows);
      setTotalItems(result.data.count);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarProdutosAvancado = (itens) => {
    const produtosIds = itens.map(({ produtoId }) => produtoId);
    const filtro = {
      id: produtosIds,
      ativado: true,
      nonPaginated: true,
    };
    produtosService.getAllProdutosSimples(filtro).then((result) => {
      if (!result.isAxiosError) {
        preencherItens(itens, result.data);
      } else {
        setLoading(false);
        responseErros(result);
      }
    });
  };

  const preencherItens = (itensBusca, produtos) => {
    const itensPreenchidos = itensBusca.map((item) => {
      const produtoBusca = produtos.find(
        (produto) => produto.id == item.produtoId,
      );
      return {
        ...item,
        descricao: produtoBusca?.descricao,
        totalItem: calcularValorTotal(item),
      };
    });
    setLoading(false);
    setItens(itensPreenchidos);
  };

  const handlePageChange = (newPage) => {
    setFiltros((prevFilters) => ({
      ...prevFilters,
      page: newPage,
    }));
    setOrcamentoselecionado(null);
    setItens([]);
  };

  const handleBusca = (searchValue) => {
    setQuery(searchValue);
    setFiltros(() => ({
      ...filtrosIniciasPaginacao,
      numeroDocumento: searchValue,
    }));
    setOrcamentoselecionado(null);
    setItens([]);
  };

  const handlePedidoClick = (pedido) => {
    setOrcamentoselecionado(pedido);
    setLoading(true);
    buscarProdutosAvancado(pedido.itens);
  };

  const handleClose = () => {
    setOpen(false);
    setItens([]);
    setOrcamentoselecionado(null);
    setFiltros({ ...filtrosIniciasPaginacao });
    setQuery("");
  };

  const totalPaginas = Math.ceil(totalItems / filtros.limit / 2);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          minHeight: "75vh",
          maxHeight: "90vh",
        },
      }}
    >
      <div className="px-3">
        <CardHeader
          title="Histórico de Orcamentos"
          action={
            <IconButton
              color="inherit"
              onClick={handleClose}
              sx={{
                backgroundColor: "#EDEEF0",
                "&:hover": { backgroundColor: "#B6BABC" },
              }}
            >
              <i
                className="ph-bold ph-x"
                style={{ fontSize: "clamp(17px, 4vw, 20px)" }}
              ></i>
            </IconButton>
          }
        />
      </div>

      <DialogContent sx={{ p: 1 }}>
        <Grid container sx={{ minHeight: "60vh" }}>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              borderRight: "1px solid",
              borderColor: "divider",
              p: 0,
            }}
          >
            <Box sx={{ px: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ mt: 1, mb: 3 }}
              >
                Lista de Orcamentos
              </Typography>

              <div className="d-flex flex-row justify-content-start align-items-center mb-3">
                <TextField
                  label="Pesquisar por Nº Documento"
                  variant="outlined"
                  size="small"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleBusca(query);
                    }
                  }}
                  sx={{ mr: 1, flex: 1 }}
                />
                <Button variant="contained" onClick={() => handleBusca(query)}>
                  Buscar
                </Button>
              </div>
              {orcamentos.length > 0 ? (
                <List sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}>
                  {orcamentos.map((pedido) => (
                    <ListItemButton
                      key={pedido.id}
                      sx={{
                        borderRadius: "8px",
                        mb: 2,
                        py: 1,
                        px: 2,
                        border:
                          orcamentoselecionado?.id === pedido.id
                            ? "2px solid #2196f3"
                            : "1px solid transparent",
                        backgroundColor:
                          orcamentoselecionado?.id === pedido.id
                            ? "#e3f2fd"
                            : "white",
                        boxShadow:
                          orcamentoselecionado?.id === pedido.id
                            ? "0 4px 12px rgba(25, 118, 210, 0.15)"
                            : "0 2px 4px rgba(0, 0, 0, 0.05)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            orcamentoselecionado?.id === pedido.id
                              ? "#e3f2fd"
                              : "#f5f5f5",
                          transform: "translateY(-1px)",
                          boxShadow: "0 6px 16px rgba(0, 0, 0, 0.1)",
                        },
                      }}
                      disableRipple
                      selected={orcamentoselecionado?.id === pedido.id}
                      onClick={() => handlePedidoClick(pedido)}
                    >
                      <ListItemText
                        primary={`Documento: ${pedido.serieDocumento}/${pedido.numeroDocumento}`}
                        secondary={`Data de Criação: ${format(
                          formatDate.received(pedido.createdAt),
                          "dd/MM/yyyy",
                        )}`}
                      />
                      {(() => {
                        switch (pedido.status) {
                          case "ABERTO":
                            return (
                              <Tooltip title="Aberto" arrow placement="right">
                                <Icon>
                                  <i
                                    className="ph-fill ph-circle"
                                    style={{ color: "#4FBB53", fontSize: 18 }}
                                  ></i>
                                </Icon>
                              </Tooltip>
                            );
                          case "FECHADO":
                            return (
                              <Tooltip title="Fechado" arrow placement="right">
                                <Icon>
                                  <i
                                    className="ph-fill ph-circle"
                                    style={{ color: "#004780", fontSize: 18 }}
                                  ></i>
                                </Icon>
                              </Tooltip>
                            );
                          case "PARCIALMENTE_FECHADO":
                            return (
                              <Tooltip
                                title="Parcialmente Fechado"
                                arrow
                                placement="right"
                              >
                                <Icon>
                                  <i
                                    className="ph-fill ph-circle"
                                    style={{ color: "#EA9D2B", fontSize: 18 }}
                                  ></i>
                                </Icon>
                              </Tooltip>
                            );
                          case "CANCELADO":
                            return (
                              <Tooltip
                                title="Cancelado"
                                arrow
                                placement="right"
                              >
                                <Icon>
                                  <i
                                    className="ph-fill ph-circle"
                                    style={{ color: "#DC3545", fontSize: 18 }}
                                  ></i>
                                </Icon>
                              </Tooltip>
                            );
                          default:
                            return null;
                        }
                      })()}
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "200px",
                    p: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", fontWeight: 500 }}
                  >
                    Nenhum pedido encontrado.
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              py: 1,
              px: 2,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "white",
            }}
          >
            {orcamentoselecionado ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    {`Itens do Pedido - ${orcamentoselecionado.serieDocumento}/${orcamentoselecionado.numeroDocumento}`}
                  </Typography>
                </Box>

                <DataGrid
                  rows={itens}
                  columns={colunasItens}
                  loading={loading}
                  disableSelectionOnClick
                  noFilter
                  disableAcoes
                  hideFooter
                />
              </Box>
            ) : (
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                  }}
                >
                  <i
                    className="ph ph-arrow-left"
                    style={{ fontSize: 36, color: "#bdbdbd" }}
                  />
                </Box>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 500, textAlign: "center" }}
                >
                  Selecione um pedido
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", maxWidth: 280 }}
                >
                  Clique em um pedido na lista ao lado para visualizar seus
                  itens detalhados.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className="d-flex justify-content-between m-3">
        <Box
          className="d-flex justify-content-center align-items-center"
          sx={{
            mx: "80px",
          }}
        >
          <Button
            onClick={() => handlePageChange(filtros.page - 1)}
            disabled={filtros.page === 1}
          >
            Anterior
          </Button>
          <Chip
            className="mx-2"
            label={` Página ${filtros.page} de ${
              totalPaginas === 0 ? 1 : totalPaginas
            }`}
          />
          <Button
            onClick={() => handlePageChange(filtros.page + 1)}
            disabled={filtros.page === totalPaginas || totalPaginas === 0}
          >
            Próxima
          </Button>
        </Box>
      </DialogActions>
      <Loading loading={loading} />
    </Dialog>
  );
};

export default HistorioClienteDialog;
